// ====================================================================================
// COMPONENT: ElectionPredictionSummary (Fixed: Clipping 'g' & Readable Headers)
// Self-contained: includes GlassContainer, StatRow, party data, and all logic.
// ====================================================================================
import { useState } from "react";
import {
  RadioTower,
  BarChart3,
  Vote,
  ChevronsUpDown,
} from "lucide-react";
import useWindowSize from 'react-use/lib/useWindowSize';
import LabourLogo from '../../assets/party_icons/Labour_logo.png';
import LibDemLogo from '../../assets/party_icons/Libby_Orange.png';
import GreenLogo from '../../assets/party_icons/Green_Party_Logo.png';
import ConservativeLogo from '../../assets/party_icons/Conservative_Logo.png';
import ReformLogo from '../../assets/party_icons/Reform_UK_Logo.png';

// --- Internal Constants ---
const partyDetails = {
  conservative: { name: 'Conservative', color: 'bg-blue-500', textColor: 'text-blue-400', hex: '#3b82f6' },
  labour: { name: 'Labour', color: 'bg-red-500', textColor: 'text-red-400', hex: '#ef4444' },
  liberal_democrat: { name: 'Liberal Democrats', color: 'bg-orange-500', textColor: 'text-orange-400', hex: '#f97316' },
  green: { name: 'Green', color: 'bg-green-500', textColor: 'text-green-400', hex: '#22c55e' },
  reform: { name: 'Reform', color: 'bg-cyan-400', textColor: 'text-cyan-400', hex: '#22d3ee' },
  snp: { name: 'SNP', color: 'bg-yellow-400', textColor: 'text-yellow-400', hex: '#facc15' },
  other: { name: 'Other', color: 'bg-slate-500', textColor: 'text-slate-400', hex: '#64748b' },
  opponentpredicted: { name: 'Opponent', color: 'bg-slate-500', textColor: 'text-slate-400', hex: '#64748b' },
};

const partyLogos = {
  labour: LabourLogo,
  liberal_democrat: LibDemLogo,
  green: GreenLogo,
  conservative: ConservativeLogo,
  reform: ReformLogo,
};

// --- Helper: Glass Container ---
const GlassContainer = ({ title, subtitle, icon: Icon, headerActions, children, className = "" }) => (
  <div className={`bg-slate-900/40 border border-slate-700/50 rounded-xl relative flex flex-col overflow-hidden h-full ${className}`}>
    <div className="px-4 py-3 border-b border-slate-800/50 flex items-center justify-between bg-slate-900/20 shrink-0">
      <div className="flex items-center gap-2.5 overflow-hidden">
        {Icon && <Icon size={16} className="text-white flex-shrink-0" />}
        <div className="flex flex-col justify-center">
          <h3 className="text-xs font-bold text-white uppercase tracking-widest truncate shadow-black drop-shadow-sm leading-tight">
            {title}
          </h3>
          {subtitle && (
            <span className="text-[10px] text-slate-300 font-medium mt-0.5 truncate leading-normal pb-0.5">
              {subtitle}
            </span>
          )}
        </div>
      </div>
      {headerActions && <div className="flex-shrink-0 ml-2">{headerActions}</div>}
    </div>
    <div className="flex-grow overflow-y-auto custom-scrollbar p-0">
      {children}
    </div>
  </div>
);

// --- Helper: Styled Data Row ---
const StatRow = ({ label, value, colorClass = "bg-slate-500", textClass = "text-slate-400" }) => {
  const barWidth = Math.min(value, 100);
  return (
    <div className="group px-4 py-2.5 border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors last:border-0">
      <div className="flex justify-between items-end mb-1.5">
        <span className={`text-xs font-bold transition-colors capitalize ${textClass} group-hover:text-white`}>
          {label}
        </span>
        <span className="text-xs font-mono font-bold text-white tabular-nums">
          {typeof value === 'number' ? value.toFixed(1) + '%' : value}
        </span>
      </div>
      {typeof value === 'number' && (
        <div className="w-full h-1 bg-slate-800 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full opacity-80 group-hover:opacity-100 transition-all duration-500 ${colorClass}`}
            style={{ width: `${barWidth}%` }}
          />
        </div>
      )}
    </div>
  );
};

// --- Main Component ---
const ElectionPredictionSummary = ({ predictionData, swingData, setDrawerData }) => {
  const { width, height } = useWindowSize();
  const [isFlipped, setIsFlipped] = useState(false);
  const [viewMode, setViewMode] = useState('forecast'); // 'forecast' | 'predictions'

  const normalizeKey = (k) => k ? k.toLowerCase().replace(/ /g, '_') : 'other';

  // --- Data Sources & Piping ---
  const leftColumnData = swingData?.summary?.national_polling_context || {};

  let rightColumnData = {};
  let seatProjection = {};
  let totalWards = 0;
  let subtitleText = "";

  if (viewMode === 'forecast') {
    rightColumnData = swingData?.summary?.campaign_forecast || {};
    seatProjection = swingData?.summary?.wards_by_predicted_winner || {};
    totalWards = swingData?.summary?.total_wards_analyzed || 0;
    subtitleText = "Calibrated with ground data from party campaigns";
  } else {
    rightColumnData = predictionData?.summary?.vote_shares || {};
    seatProjection = predictionData?.summary?.wards_leading_by_party || {};
    totalWards = predictionData?.summary?.total_wards || 0;
    subtitleText = "Demographic benchmark";
  }

  // --- KPI Box Logic ---
  const seatKeys = Object.keys(seatProjection);
  const seatsLeaderKey = seatKeys.length > 0
    ? seatKeys.reduce((a, b) => (seatProjection[b] || 0) > (seatProjection[a] || 0) ? b : a)
    : 'other';

  const leaderKeyNormalized = normalizeKey(seatsLeaderKey);

  let leaderDetails = partyDetails.other;
  if (leaderKeyNormalized === 'opponentpredicted' || leaderKeyNormalized === 'opponent') {
    leaderDetails = partyDetails.opponentpredicted;
  } else {
    leaderDetails = partyDetails[leaderKeyNormalized] || partyDetails.other;
  }

  const seatsLeader = {
    party: seatsLeaderKey,
    ...leaderDetails,
    value: seatProjection[seatsLeaderKey] || 0,
  };

  const displayTotalSeats = totalWards > 0 ? totalWards : 1;

  const seatsDistribution = Object.keys(seatProjection)
    .map(key => {
      const normKey = normalizeKey(key);
      const dKey = (normKey === 'opponentpredicted' || normKey === 'opponent') ? 'opponentpredicted' : normKey;
      return {
        ...partyDetails[dKey] || partyDetails.other,
        id: key,
        seats: seatProjection[key] || 0,
      };
    })
    .filter(p => p.seats > 0)
    .sort((a, b) => b.seats - a.seats);

  const glowStyle = {
    boxShadow: `0 0 30px -5px ${seatsLeader.hex}40`,
  };

  return (
    <div className="panel p-0 bg-transparent border-0 grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 relative items-stretch mb-8 min-h-[320px] lg:h-[400px]">

      {/* --- LEFT COLUMN: National Polling --- */}
      <div className="lg:col-span-2 h-full min-w-0 min-h-[200px]">
        <GlassContainer title="National Polling" icon={RadioTower}>
          {Object.keys(leftColumnData).length > 0 ? (
            Object.entries(leftColumnData)
              .sort(([, a], [, b]) => b - a)
              .map(([party, value]) => {
                const pKey = normalizeKey(party);
                const details = partyDetails[pKey] || partyDetails.other;
                return (
                  <StatRow
                    key={party}
                    label={details.name}
                    value={value}
                    colorClass={details.color}
                    textClass={details.textColor}
                  />
                );
              })
          ) : (
            <div className="flex h-full items-center justify-center text-[10px] text-slate-500 italic">No polling data</div>
          )}
        </GlassContainer>
      </div>

      {/* --- CENTER COLUMN: Reactive KPI Card (flip) --- */}
      <div className="lg:col-span-1 h-full min-h-[280px] flex flex-col min-w-0">
        <div className="perspective-[1000px] group cursor-pointer h-full relative" onClick={() => setIsFlipped(!isFlipped)}>
          <div
            className={`relative w-full h-full transition-transform duration-700`}
            style={{ transformStyle: 'preserve-3d', transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
          >
            {/* FRONT FACE */}
            <div
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-md rounded-xl border border-slate-700/50 overflow-hidden flex flex-col items-center justify-between p-4 hover:border-slate-500 transition-colors"
              style={{ ...glowStyle, backfaceVisibility: 'hidden' }}
            >
              <div className="absolute top-0 left-0 right-0 h-1/2 opacity-20 pointer-events-none blur-2xl"
                style={{ background: `radial-gradient(circle at top, ${seatsLeader.hex}, transparent)` }}>
              </div>

              <div className="relative z-10 flex flex-col items-center w-full mt-4">
                <div className="text-[9px] font-bold uppercase tracking-widest text-slate-300 mb-4 bg-slate-950/40 px-2 py-0.5 rounded border border-slate-800">
                  Projected Outcome
                </div>
                <img
                  src={partyLogos[leaderKeyNormalized]}
                  alt={seatsLeader.name}
                  className="h-16 w-auto object-contain drop-shadow-lg mb-4"
                />
                <div className="text-center">
                  <div className="flex items-baseline justify-center text-white font-bold tracking-tighter">
                    <span className="text-5xl drop-shadow-md">{seatsLeader.value}</span>
                    <span className="text-xl text-slate-500 ml-1">/{displayTotalSeats}</span>
                  </div>
                  <div className={`text-sm font-bold ${seatsLeader.textColor} mt-1 uppercase tracking-wide`}>
                    {seatsLeader.name} Win
                  </div>
                </div>
              </div>

              <div className="w-full relative z-10 mb-2">
                <div className="flex h-1.5 rounded-full overflow-hidden bg-slate-950/50 border border-slate-800/50">
                  {seatsDistribution.map(party => (
                    <div key={party.id} className={party.color} style={{ width: `${(party.seats / displayTotalSeats) * 100}%` }}></div>
                  ))}
                </div>
                <div className="mt-2 text-[12px] text-slate-500 flex items-center justify-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                  <ChevronsUpDown size={10} /> Flip for breakdown
                </div>
              </div>
            </div>

            {/* BACK FACE */}
            <div
              className="absolute inset-0 bg-slate-900/90 backdrop-blur-xl rounded-xl border border-slate-700/50 flex flex-col overflow-hidden"
              style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
            >
              <div className="px-4 py-3 border-b border-slate-800 bg-slate-950/30 flex items-center justify-center gap-2">
                <Vote size={14} className="text-slate-400" />
                <h3 className="text-[10px] font-bold text-slate-200 uppercase tracking-widest">Seat Breakdown</h3>
              </div>

              <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                {seatsDistribution.map((party) => {
                  const lowerId = normalizeKey(party.id);
                  const SmallLogo = (lowerId === 'opponentpredicted' || lowerId === 'opponent') ? null : partyLogos[lowerId];
                  return (
                    <div key={party.id} className="flex justify-between items-center p-2 rounded-lg bg-slate-800/20 border border-slate-800 hover:bg-slate-800/50 transition-colors">
                      <div className="flex items-center gap-2">
                        {SmallLogo ? (
                          <img src={SmallLogo} alt={party.name} className="w-4 h-4 object-contain opacity-90" />
                        ) : (
                          <div className={`w-1.5 h-1.5 rounded-full ${party.color}`}></div>
                        )}
                        <span className={`text-[10px] font-bold uppercase ${party.textColor}`}>
                          {party.name}
                        </span>
                      </div>
                      <span className="text-xs font-mono font-bold text-white">
                        {party.seats}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- RIGHT COLUMN: Toggle + Data List --- */}
      <div className="lg:col-span-2 h-full min-w-0 min-h-[200px]">
        <GlassContainer
          title={viewMode === 'forecast' ? 'Campaign Forecast' : 'Vote Prediction'}
          subtitle={subtitleText}
          icon={BarChart3}
          headerActions={
            <div className="flex bg-slate-950/50 rounded-lg p-0.5 border border-slate-700/50">
              <button
                onClick={(e) => { e.stopPropagation(); setViewMode('forecast'); }}
                className={`px-2 py-1 rounded-md text-[9px] font-bold uppercase tracking-wider transition-all duration-300 ${
                  viewMode === 'forecast'
                    ? 'bg-slate-700 text-white shadow-sm ring-1 ring-slate-600'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                Forecast
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setViewMode('predictions'); }}
                className={`px-2 py-1 rounded-md text-[9px] font-bold uppercase tracking-wider transition-all duration-300 ${
                  viewMode === 'predictions'
                    ? 'bg-slate-700 text-emerald-300 shadow-sm ring-1 ring-slate-600'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                Predictions
              </button>
            </div>
          }
        >
          {Object.keys(rightColumnData).length > 0 ? (
            Object.entries(rightColumnData)
              .sort(([, a], [, b]) => b - a)
              .map(([party, value]) => {
                const pKey = normalizeKey(party);
                const details = partyDetails[pKey] || partyDetails.other;
                return (
                  <StatRow
                    key={party}
                    label={details.name}
                    value={value}
                    colorClass={details.color}
                    textClass={details.textColor}
                  />
                );
              })
          ) : (
            <div className="flex h-full items-center justify-center text-[10px] text-slate-500 italic">No projection data</div>
          )}
        </GlassContainer>
      </div>
    </div>
  );
};

export default ElectionPredictionSummary;
